import React, { useState } from 'react';

import { ButtonRounded, Checkbox } from 'is24-corecss';
import { useTranslation } from 'react-i18next';
import { useLoaderData } from 'react-router-dom';
import CompanyFormSelect, {
  CompanyFormOptionKey,
  defaultCompanyFormOption,
} from '../../components/companyFormSelect/CompanyFormSelect';
import FileUpload from '../../components/fileUpload/FileUpload';
import IbanInput from '../../components/ibanInput/IbanInput';

interface KybLandingPageLoaderData {
  shouldShowKybPage: boolean;
}

export async function loader(): Promise<KybLandingPageLoaderData> {
  return {
    shouldShowKybPage: true,
  };
}

const KybLandingPage: React.FC = () => {
  const { shouldShowKybPage } = useLoaderData() as KybLandingPageLoaderData;
  const { t } = useTranslation();
  const [companyForm, setCompanyForm] = useState<CompanyFormOptionKey>(defaultCompanyFormOption);
  const [identityDocumentFileName, setIdentityDocumentFileName] = useState<string | null>(null);
  const [registerExtractFileName, setRegisterExtractFileName] = useState<string | null>(null);
  const shouldShowIbanInput =
    companyForm === 'unspecified' || companyForm === 'unregisteredCompany' || companyForm === 'other';
  const shouldShowRegisterExtractUpload = companyForm !== 'unspecified' && companyForm !== 'unregisteredCompany';

  if (!shouldShowKybPage) {
    return null;
  }

  return (
    <div className='margin-top-l margin-bottom-xxl'>
      <div className='font-heading-medium-bold margin-bottom-xl'>{t('KybLandingPage.heading')}</div>
      <div className='margin-bottom-xxl'>{t('KybLandingPage.intro')}</div>

      <div className='font-heading-xsmall-bold margin-bottom-xl'>{t('KybLandingPage.identityDocumentLabel')}</div>
      <div className='margin-bottom-l'>{t('KybLandingPage.identityDocumentDescription')}</div>
      <div className='grid grid-flex'>
        <div className='grid-item one-whole lap-one-half desk-one-third'>
          <FileUpload
            title={t('FileUpload.identityDocumentTitle')}
            subtitle={identityDocumentFileName ?? t('FileUpload.description')}
            status={identityDocumentFileName ? 'uploaded' : 'empty'}
            onFileSelect={(files) => setIdentityDocumentFileName(files[0]?.name ?? null)}
          />
        </div>
      </div>

      <div className='font-heading-xsmall-bold margin-top-xxl margin-bottom-l'>
        {t('KybLandingPage.companyFormLabel')}
      </div>
      <div className='grid grid-flex'>
        <div className='grid-item one-whole lap-one-half desk-one-half'>
          <CompanyFormSelect value={companyForm} onChange={setCompanyForm} />
        </div>
      </div>

      {shouldShowRegisterExtractUpload && (
        <div className='grid grid-flex'>
          <div className='grid-item one-whole lap-one-half desk-one-half margin-top-l margin-bottom-l'>
            {t('KybLandingPage.registerExtractDescription')}
          </div>
          <div className='grid-item one-whole margin-bottom-m'>{t('KybLandingPage.registerExtractUploadLabel')}</div>
          <div className='grid-item one-whole lap-one-half desk-one-third'>
            <FileUpload
              title={t('FileUpload.registerExtractTitle')}
              subtitle={registerExtractFileName ?? t('FileUpload.description')}
              status={registerExtractFileName ? 'uploaded' : 'empty'}
              onFileSelect={(files) => setRegisterExtractFileName(files[0]?.name ?? null)}
            />
          </div>
        </div>
      )}

      {shouldShowIbanInput && (
        <div className='grid grid-flex margin-top-l margin-bottom-m'>
          <div className='grid-item one-whole lap-one-half desk-one-half'>
            <IbanInput />
          </div>
        </div>
      )}

      <div className='font-heading-xsmall-bold margin-top-xxl margin-bottom-m'>
        {t('KybLandingPage.selfCertificationLabel')}
      </div>
      <Checkbox id='checkbox-1' label={t('KybLandingPage.selfCertificationText')} onChange={() => {}} />

      <div className='margin-top-xxl'>{t('KybLandingPage.continueHint')}</div>
      <hr className='divider margin-top-xxl margin-bottom-m' />
      <div className='grid grid-flex grid-justify-end'>
        <div className='grid-item palm-one-whole'>
          <ButtonRounded label={t('KybLandingPage.continueButton')} variant='filledBrand' widthMode='expand' />
        </div>
      </div>
    </div>
  );
};

export default KybLandingPage;
